import { OauthService } from '../../api/services/oauth.service';
import { InputEventUtils } from '../../utils/input_event_utils';
import { oauthAccountView } from './oauth-account.view';
/**
 * OAuth sign-in control bound to the `#oauth-account` button.
 *
 * Sign-in uses a multi-domain flow: clicking the button (when logged out)
 * redirects the top-level page to the central auth selector on a different
 * domain. After the provider callback the browser is redirected back here, where
 * the resulting token is pulled over via a hidden postMessage relay iframe and
 * stored under the shared `accesstoken` key — so the rest of the app logs in
 * exactly as it would for an email/password sign-in. When already logged in,
 * clicking the button signs out. See {@link OauthService} for the wire contract.
 */
export class OauthAccount {
    constructor() {
        this.user = null;
        this.oauthService = new OauthService();
        this.buttonEl = document.getElementById('oauth-account');
        if (!this.buttonEl) {
            return;
        }
        this.onButtonClick = this.onButtonClick.bind(this);
        this.buttonEl.innerHTML = oauthAccountView();
        InputEventUtils.addSelectEvents(this.buttonEl, this.onButtonClick);
        this.buttonEl.classList.remove('is-hidden');
        this.setLoggedInState(null);
        if (this.oauthService.isReturningFromAuth()) {
            void this.completeSignIn();
        }
        else {
            void this.loadSession();
        }
    }
    onButtonClick() {
        if (this.user) {
            this.signOut();
            return;
        }
        this.oauthService.beginSignIn();
    }
    completeSignIn() {
        return this.oauthService
            .completeSignIn()
            .then(() => this.loadSession())
            .catch(() => this.setLoggedInState(null));
    }
    loadSession() {
        return this.oauthService
            .getSession()
            .then(({ user }) => this.setLoggedInState(user))
            .catch(() => this.setLoggedInState(null));
    }
    signOut() {
        this.oauthService.signOut();
        this.setLoggedInState(null);
    }
    setLoggedInState(user) {
        this.user = user;
        if (!this.buttonEl) {
            return;
        }
        const isLoggedIn = !!user;
        this.buttonEl.classList.toggle('is-logged-in', isLoggedIn);
        this.buttonEl.setAttribute('aria-label', isLoggedIn ? `Sign out ${user.display_name || user.email || ''}`.trim() : 'Sign in');
    }
    dispose() {
        var _a;
        (_a = this.buttonEl) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this.onButtonClick);
    }
}
